import { useEffect, useRef } from 'react';
import { useFetcher } from 'react-router';
import { cn } from '@rocco/utils/cn';
import { ReactComponent as ArrowRight } from '@rocco/icons/svg/arrow/arrow-right.svg';

/** Real newsletter endpoint (RegisterForNewsLetter), shared with the site footer. */
const SUBSCRIPTION_ACTION = '/remix-api/user/subscription';

interface SubscribeFieldProps {
    /**
     * magazine = light pill + dark "Subscribe →" button (white section).
     * footer = solid dark pill + off-white arrow-only button (dark footer).
     * article = magazine's shape on the article revamp's rem scale, sized in
     * base + md: steps rather than the home page's vw units.
     */
    variant?: 'magazine' | 'footer' | 'article';
    className?: string;
    /** Fired once after a successful subscription (e.g. to refetch status). */
    onSuccess?: () => void;
}

/**
 * Email subscribe pill posting to the existing newsletter endpoint via a fetcher
 * — a working subscription rather than a stub. Two visual variants share the
 * submit/success/error logic (Figma magazine field + footer field).
 */
export const SubscribeField = ({
    variant = 'magazine',
    className,
    onSuccess,
}: SubscribeFieldProps) => {
    const fetcher = useFetcher<{ success?: boolean; message?: string }>();
    const isSubmitting = fetcher.state !== 'idle';
    const isSuccess = fetcher.data?.success === true;
    const error =
        fetcher.state === 'idle' && fetcher.data && !fetcher.data.success
            ? fetcher.data.message
            : undefined;
    const isFooter = variant === 'footer';
    const isArticle = variant === 'article';

    // Notify the parent once, on the transition into success, so it can refetch
    // derived state (e.g. the member's subscription status) without re-firing on
    // every later render while the success message stays mounted.
    const notifiedRef = useRef(false);
    useEffect(() => {
        if (isSuccess && !notifiedRef.current) {
            notifiedRef.current = true;
            onSuccess?.();
        }
    }, [isSuccess, onSuccess]);

    if (isSuccess) {
        return (
            <p
                className={cn(
                    'm-0 font-sans font-medium',
                    isArticle
                        ? 'text-base md:text-[1.419rem]'
                        : 'max-md:text-sm md:text-[1.04vw]',
                    isFooter ? 'text-offwhite' : 'text-[#1f1f21]',
                    className,
                )}
                role="status"
                aria-live="polite"
            >
                You’re subscribed — thanks for joining us.
            </p>
        );
    }

    return (
        <div className={cn('flex w-full flex-col gap-2', className)}>
            <fetcher.Form
                method="post"
                action={SUBSCRIPTION_ACTION}
                className={cn(
                    'flex w-full items-center justify-between rounded-[1.375rem]',
                    isFooter &&
                        'bg-white/5 px-6 max-md:h-[11.23vw] md:h-[54px] md:px-[35px]',
                    // 72px pill, 34.61px inset, 6.39px button gutter (Figma
                    // 2736:57257) on the article revamp's /16 rem scale.
                    isArticle &&
                        'h-14 bg-[#f6f6f6] pr-[0.3rem] pl-5 md:h-[4.5rem] md:pr-[0.4rem] md:pl-[2.163rem]',
                    !isFooter &&
                        !isArticle &&
                        'bg-[#f6f6f6] max-md:h-[9.18vw] max-md:pr-[0.97vw] max-md:pl-[3.4vw] md:h-[3.75vw] md:pr-[0.417vw] md:pl-[1.823vw]',
                )}
            >
                <input
                    type="email"
                    name="email"
                    required
                    placeholder="Email Address"
                    aria-label="Email address for newsletter subscription"
                    autoComplete="email"
                    className={cn(
                        'min-w-0 flex-1 bg-transparent font-sans font-medium focus:outline-hidden',
                        isFooter &&
                            'text-base text-offwhite placeholder:text-offwhite',
                        isArticle &&
                            'text-base tracking-[-0.03em] text-[#181818] placeholder:text-[#181818]/30 md:text-[1.419rem]',
                        !isFooter &&
                            !isArticle &&
                            'text-[#181818] placeholder:text-[#181818]/30 max-md:text-[2.9vw] md:text-[1.182vw]',
                        !isArticle && 'tracking-[0.02em]',
                    )}
                />
                {isFooter ? (
                    <button
                        type="submit"
                        aria-label="Subscribe"
                        disabled={isSubmitting}
                        className="shrink-0 transition-opacity hover:opacity-70 disabled:opacity-40"
                    >
                        <ArrowRight
                            className="size-4 fill-offwhite text-offwhite"
                            aria-hidden="true"
                        />
                    </button>
                ) : (
                    <button
                        type="submit"
                        disabled={isSubmitting}
                        className={cn(
                            'flex shrink-0 items-center justify-center rounded-[1.375rem] bg-[#181818] font-sans font-medium whitespace-nowrap text-white shadow-[0px_3.243px_11.351px_rgba(0,0,0,0.11)] transition-opacity hover:opacity-90 disabled:opacity-50',
                            // 60px tall, 230.27px wide button (Figma 2736:57259).
                            isArticle
                                ? 'h-[2.875rem] gap-2 px-5 text-[0.9375rem] tracking-[-0.03em] md:h-[3.75rem] md:min-w-[14.392rem] md:gap-[0.5rem] md:pr-[1.098rem] md:pl-[1.723rem] md:text-[1.419rem]'
                                : 'max-md:h-[7.66vw] max-md:gap-[0.97vw] max-md:px-[2.42vw] max-md:text-[2.66vw] md:h-[3.125vw] md:min-w-[11.99vw] md:gap-[0.5vw] md:px-[1.436vw] md:text-[1.182vw]',
                        )}
                    >
                        Subscribe
                        <ArrowRight
                            className={cn(
                                'fill-white text-white',
                                isArticle
                                    ? 'size-4 md:h-[2.125rem] md:w-[1.75rem]'
                                    : 'max-md:size-[2.66vw] md:h-[0.833vw] md:w-[1.1vw]',
                            )}
                            aria-hidden="true"
                        />
                    </button>
                )}
            </fetcher.Form>
            {error && (
                <p
                    className={cn(
                        'm-0 px-6 font-sans',
                        isArticle
                            ? 'text-xs md:text-[0.875rem]'
                            : 'max-md:text-xs md:text-[0.833vw]',
                        isFooter ? 'text-red-300' : 'text-red-600',
                    )}
                    role="alert"
                >
                    {error}
                </p>
            )}
        </div>
    );
};
